import styled from '@emotion/styled'
import {
  Box,
  Button,
  Grid,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { DateTimePicker } from '@mui/x-date-pickers'
import { Error } from 'components/Error'
import { Loading } from 'components/Loading'
import { useEffect } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
  useTenderProtocolQuery,
  useUpdateTenderProtocolMutation,
} from 'services/final-protocol'
import { ITenderProtocolData } from 'types/final-protocol'
import { ETenderType, ITender } from 'types/tender'
import { CommissionMembersTable } from './tables/commission-members-table'
import { InvitationItemsTable } from './tables/invitation-items'
import { SubmitterOrganizationsInfoTable } from './tables/submitter-organization-info-table'
import { TechnicalEvaluationResultsTable } from './tables/technical-evaluation-results-table'
import { CommercialEvaluationResultsTable } from './tables/commercial-evaluation-results-table'
import { TenderEvaluationCriteriaTable } from './tables/tender-evaluation-criteria-table'
import {
  formatDateTimePickerValue,
  formattedTime,
  formatTimeStampToDateWithTime,
} from 'hooks/date/formattedTime'
import { RejectedSubmitterOrganizationsTable } from './tables/rejected-submitter-organizations-table-table'
import { ProcurementEvaluationScoreTable } from './tables/procurement-evaluation-score-table'
import { SubmitterOrganizationRanksTable } from './tables/submitter-organization-ranks-table'
import { CommissionEvaluationsProtocolTable } from './tables/commission-evaluations-protocol-table'
import { toast } from 'react-toastify'
import { DocRequirementEligibilityEvaluationTable } from './tables/doc-requirement-eligibility-evaluation-table'
import { FileUpload } from 'components/FileUpload'
// @ts-ignore
// import html2pdf from 'html2pdf.js'

const StyledLabel = styled(Typography)(() => ({
  fontSize: '16px',
  fontWeight: 600,
  marginBottom: '12px',
}))

enum ProcurementMethod {
  OPEN_TENDER = 'OPEN_TENDER',
  PRICE_INQUIRY = 'PRICE_INQUIRY',
  TWO_STAGE_TENDER = 'TWO_STAGE_TENDER',
}

type Props = {
  tender: ITender
}

export const FinalProtocol = ({ tender }: Props) => {
  const { t } = useTranslation()

  const { data, isLoading, isError, isSuccess, refetch } = useTenderProtocolQuery(
    { id: tender.id },
    {
      skip: !tender,
    }
  )

  const [
    updateTenderProtocol,
    {
      isSuccess: isUpdateTenderProtocolSuccess,
      isError: isUpdateTenderProtocolError,
    },
  ] = useUpdateTenderProtocolMutation()

  const methods = useForm<ITenderProtocolData>({
    defaultValues: {
      tenderOrgName: '',
      tenderOrgAddress: '',
      tenderOrgPrincipal: '',
      protocolDate: '',
      tenderNumber: '',
      tenderName: '',
      tenderMethod: '' as ETenderType,
      estimatedPriceAmount: '',
      participationFeeAmount: '',
      announcementDate: '',
      changesOnTermsConditions: '',
      commissionOrderInfo: {
        orderDateAndNumber: '',
        orderFileId: null,
      },
      commissionMembers: [],
      invitations: [],
      submitterOrganizations: [],
      envelopeOpeningTime: '',
      technicalAssessments: [],
      technicalAssessmentNote: '',
      procurementEvaluationResults: [],
      procurementEvaluationResultNote: '',
      rejectedSubmitterOrganizations: [],
      procurementCommitteeLetterNumber: '',
      procurementEvaluationScores: [],
      submitterOrganizationRanks: [],
      procurementDivisionDecision: '',
      termsGuaranteePercentage: '',
      contractGuarantee: '',
      downPaymentAmount: '',
      downPaymentGuarantee: '',
      complaintsData: '',
      commissionExperts: '',
      commissionExpertNote: '',
      commissionEvaluations: [],
      tenderEvaluationCriteria: {},
      docRequirementEligibilityEvaluation: {},
    },
  })

  useEffect(() => {
    if (data && isSuccess) {
      reset({
        tenderOrgName: data.data.tenderOrgName,
        tenderOrgAddress: data.data.tenderOrgAddress,
        tenderOrgPrincipal: data.data.tenderOrgPrincipal,
        protocolDate: data.data.protocolDate
          ? formatDateTimePickerValue(data.data.protocolDate)
          : null,
        tenderNumber: data.data.tenderNumber,
        tenderName: data.data.tenderName,
        tenderMethod: data.data.tenderMethod,
        estimatedPriceAmount: data.data.estimatedPriceAmount,
        participationFeeAmount: data.data.participationFeeAmount,
        announcementDate: data.data.announcementDate
          ? formatDateTimePickerValue(data.data.announcementDate)
          : null,
        changesOnTermsConditions: data.data.changesOnTermsConditions,
        commissionOrderInfo: data.data.commissionOrderInfo,
        commissionMembers: data.data.commissionMembers,
        invitations: data.data.invitations,
        submitterOrganizations: data.data.submitterOrganizations,
        envelopeOpeningTime: data.data.envelopeOpeningTime
          ? formatDateTimePickerValue(data.data.envelopeOpeningTime)
          : null,
        technicalAssessments: data.data.technicalAssessments,
        technicalAssessmentNote: data.data.technicalAssessmentNote,
        procurementEvaluationResults: data.data.procurementEvaluationResults,
        procurementEvaluationResultNote: data.data.procurementEvaluationResultNote,
        rejectedSubmitterOrganizations: data.data.rejectedSubmitterOrganizations,
        procurementCommitteeLetterNumber: data.data.procurementCommitteeLetterNumber,
        procurementEvaluationScores: data.data.procurementEvaluationScores,
        submitterOrganizationRanks: data.data.submitterOrganizationRanks,
        procurementDivisionDecision: data.data.procurementDivisionDecision,
        termsGuaranteePercentage: data.data.termsGuaranteePercentage,
        contractGuarantee: data.data.contractGuarantee,
        downPaymentAmount: data.data.downPaymentAmount,
        downPaymentGuarantee: data.data.downPaymentGuarantee,
        complaintsData: data.data.complaintsData,
        commissionExperts: data.data.commissionExperts,
        commissionExpertNote: data.data.commissionExpertNote,
        commissionEvaluations: data.data.commissionEvaluations,
        tenderEvaluationCriteria: data.data.tenderEvaluationCriteria,
        docRequirementEligibilityEvaluation:
          data.data.docRequirementEligibilityEvaluation,
      })
    }
  }, [data, isSuccess])

  const onSubmit = async (values: ITenderProtocolData) => {
    const result = { ...data }
    result.data = values
    updateTenderProtocol(result)
    refetch()
  }

  const handleDownload = () => {
    // const element = document.getElementById('content')
    // html2pdf().from(element).save()
  }

  const { control, handleSubmit, reset, watch } = methods

  const orderInfoFileId = watch('commissionOrderInfo.orderFileId')

  useEffect(() => {
    if (isUpdateTenderProtocolSuccess) {
      toast.success(t('operation-success'))
    }
    if (isUpdateTenderProtocolError) {
      toast.error(t('failedToCreate'))
    }
  }, [isUpdateTenderProtocolSuccess, isUpdateTenderProtocolError, t])

  if (isLoading) {
    return <Loading fullPage={false} />
  }
  if (isError) {
    return <Error fullPage={false} />
  }
  if (data === undefined) {
    return <></>
  }

  return (
    <Stack id="content" spacing={2}>
      <Typography sx={{ textAlign: 'center' }} fontWeight="bold" variant="h6">
        {t('final-protocol-title')}
      </Typography>
      <FormProvider {...methods}>
        <form>
          <Stack spacing={3}>
            <Grid container rowGap={'30px'} columnGap={'20px'}>
              <Grid item xs={6}>
                <StyledLabel>1. {t('tender-organization-name')}</StyledLabel>
                <Controller
                  name="tenderOrgName"
                  control={control}
                  render={({ field }) => <TextField {...field} fullWidth />}
                />
              </Grid>
              <Grid item xs={6}>
                <StyledLabel>2. {t('tender-organization-address')}</StyledLabel>
                <Controller
                  name="tenderOrgAddress"
                  control={control}
                  render={({ field }) => <TextField {...field} fullWidth />}
                />
              </Grid>
              <Grid item xs={6}>
                <StyledLabel>3. {t('tender-organization-director')}</StyledLabel>
                <Controller
                  name="tenderOrgPrincipal"
                  control={control}
                  render={({ field }) => <TextField {...field} fullWidth />}
                />
              </Grid>
              <Grid item xs={6}>
                <StyledLabel>4. {t('protocol-date')}</StyledLabel>
                <Controller
                  control={control}
                  name="protocolDate"
                  render={({ field }) => (
                    <DateTimePicker
                      {...field}
                      ampm={false}
                      slotProps={{ textField: { error: false } }}
                      onChange={field.onChange}
                      timeSteps={{
                        hours: 1,
                        minutes: 1,
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                <StyledLabel>5. {t('tender-number')}</StyledLabel>
                <Controller
                  name="tenderNumber"
                  control={control}
                  render={({ field }) => <TextField {...field} fullWidth />}
                />
              </Grid>
              <Grid item xs={6}>
                <StyledLabel>6. {t('tender-type')}</StyledLabel>
                <Controller
                  name="tenderMethod"
                  control={control}
                  render={({ field }) => (
                    <Select {...field} labelId="method-select-helper-label">
                      {Object.keys(ProcurementMethod).map((method) => (
                        <MenuItem value={method} key={method}>
                          {t(method)}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                <StyledLabel>7. {t('estimated-price-amount-azn')}</StyledLabel>
                <Controller
                  name="estimatedPriceAmount"
                  control={control}
                  render={({ field }) => <TextField {...field} fullWidth />}
                />
              </Grid>
              <Grid item xs={6}>
                <StyledLabel>8. {t('participation-fee')}</StyledLabel>
                <Controller
                  name="participationFeeAmount"
                  control={control}
                  render={({ field }) => <TextField {...field} fullWidth />}
                />
              </Grid>
              <Grid item xs={6}>
                <StyledLabel>9. {t('tender-publish-datetime')}</StyledLabel>
                <Controller
                  control={control}
                  name="announcementDate"
                  render={({ field }) => (
                    <DateTimePicker
                      {...field}
                      ampm={false}
                      slotProps={{ textField: { error: false } }}
                      onChange={field.onChange}
                      timeSteps={{
                        hours: 1,
                        minutes: 1,
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                <StyledLabel>10. {t('terms-and-condition-changes')}</StyledLabel>
                <Controller
                  name="changesOnTermsConditions"
                  control={control}
                  render={({ field }) => <TextField {...field} fullWidth />}
                />
              </Grid>
              <Grid item xs={6} display="flex" columnGap="8px">
                <Box>
                  <StyledLabel>11. {t('commission-create-date')}</StyledLabel>
                  <Controller
                    control={control}
                    name="commissionOrderInfo.orderDateAndNumber"
                    render={({ field }) => <TextField {...field} fullWidth />}
                  />
                </Box>
                <Box>
                  {!!orderInfoFileId && (
                    <Box alignSelf="flex-end">
                      <FileUpload
                        fileId={orderInfoFileId}
                        disableUpload
                        disableDelete
                        isDisabled
                      />
                    </Box>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12}>
                <StyledLabel>12. {t('commission-members')}</StyledLabel>
                <CommissionMembersTable protocolData={data.data} />
              </Grid>
              <Grid item xs={12}>
                <StyledLabel>13. {t('protocol-invitation-items')}</StyledLabel>
                <InvitationItemsTable protocolData={data.data} />
              </Grid>
              <Grid item xs={12}>
                <StyledLabel>14. {t('submitted-organizations-info')}</StyledLabel>
                <SubmitterOrganizationsInfoTable protocolData={data.data} />
              </Grid>
              <Grid item xs={6}>
                <StyledLabel>15. {t('submissions-opening-date-time')}</StyledLabel>
                <Controller
                  control={control}
                  name="envelopeOpeningTime"
                  render={({ field }) => (
                    <DateTimePicker
                      {...field}
                      ampm={false}
                      slotProps={{ textField: { error: false } }}
                      onChange={field.onChange}
                      timeSteps={{
                        hours: 1,
                        minutes: 1,
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <StyledLabel>16. {t('technical-evaluation-results')}</StyledLabel>
                <TechnicalEvaluationResultsTable protocolData={data.data} />
                <StyledLabel sx={{ marginBottom: '0', marginTop: '6px' }}>
                  {t('note-about-result')}
                  <Controller
                    control={control}
                    name="technicalAssessmentNote"
                    render={({ field }) => <TextField {...field} fullWidth />}
                  />
                </StyledLabel>
              </Grid>
              <Grid item xs={12}>
                <StyledLabel>17. {t('commercial-evaluation-results')}</StyledLabel>
                <CommercialEvaluationResultsTable protocolData={data.data} />
                <StyledLabel sx={{ marginBottom: '0', marginTop: '6px' }}>
                  {t('note-about-commercial-result')}
                  <Controller
                    control={control}
                    name="procurementEvaluationResultNote"
                    render={({ field }) => <TextField {...field} fullWidth />}
                  />
                </StyledLabel>
              </Grid>
              <Grid item xs={12}>
                <StyledLabel>18. {t('procurement-score-title')}</StyledLabel>
                <TenderEvaluationCriteriaTable protocolData={data.data} />
              </Grid>
              <Grid item xs={12}>
                <StyledLabel>19. {t('requirement-eligibility')}</StyledLabel>
                <DocRequirementEligibilityEvaluationTable protocolData={data.data} />
              </Grid>
              <Grid item xs={12}>
                <StyledLabel>
                  20. {t('rejected-submitter-organizations')}
                </StyledLabel>
                <RejectedSubmitterOrganizationsTable protocolData={data.data} />
              </Grid>
              <Grid item xs={6}>
                <StyledLabel>
                  21. {t('procurement-committee-letter-number')}
                </StyledLabel>
                <Controller
                  name="procurementCommitteeLetterNumber"
                  control={control}
                  render={({ field }) => <TextField {...field} fullWidth />}
                />
              </Grid>
              <Grid item xs={12}>
                <StyledLabel>22. {t('procurement-evaluations-score')}</StyledLabel>
                <ProcurementEvaluationScoreTable protocolData={data.data} />
              </Grid>
              <Grid item xs={12}>
                <StyledLabel>23. {t('submitter-organization-ranks')}</StyledLabel>
                <SubmitterOrganizationRanksTable protocolData={data.data} />
              </Grid>
              <Grid item xs={6}>
                <StyledLabel>24. {t('procurement-division-decision')}</StyledLabel>
                <Controller
                  name="procurementDivisionDecision"
                  control={control}
                  render={({ field }) => <TextField {...field} fullWidth />}
                />
              </Grid>
              <Grid item xs={6}>
                <StyledLabel>25. {t('terms-guarantee-percentage')}</StyledLabel>
                <Controller
                  name="termsGuaranteePercentage"
                  control={control}
                  render={({ field }) => <TextField {...field} fullWidth />}
                />
              </Grid>
              <Grid item xs={6}>
                <StyledLabel>26. {t('contract-guarantee')}</StyledLabel>
                <Controller
                  name="contractGuarantee"
                  control={control}
                  render={({ field }) => <TextField {...field} fullWidth />}
                />
              </Grid>
              <Grid item xs={6}>
                <StyledLabel>27. {t('down-payment-amount')}</StyledLabel>
                <Controller
                  name="downPaymentAmount"
                  control={control}
                  render={({ field }) => <TextField {...field} fullWidth />}
                />
              </Grid>
              <Grid item xs={6}>
                <StyledLabel>28. {t('down-payment-guarantee')}</StyledLabel>
                <Controller
                  name="downPaymentGuarantee"
                  control={control}
                  render={({ field }) => <TextField {...field} fullWidth />}
                />
              </Grid>
              <Grid item xs={6}>
                <StyledLabel>29. {t('complaints-data')}</StyledLabel>
                <Controller
                  name="complaintsData"
                  control={control}
                  render={({ field }) => <TextField {...field} fullWidth />}
                />
              </Grid>
              <Grid item xs={6}>
                <StyledLabel>30. {t('commission-experts')}</StyledLabel>
                <Controller
                  name="commissionExperts"
                  control={control}
                  render={({ field }) => <TextField {...field} fullWidth />}
                />
              </Grid>
              <Grid item xs={6}>
                <StyledLabel>31. {t('commission-expert-note')}</StyledLabel>
                <Controller
                  name="commissionExpertNote"
                  control={control}
                  render={({ field }) => <TextField {...field} fullWidth />}
                />
              </Grid>
              <Grid item xs={12}>
                <StyledLabel>32. {t('commission-evaluations')}</StyledLabel>
                <CommissionEvaluationsProtocolTable protocolData={data.data} />
              </Grid>
            </Grid>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '15px' }}>
              {/* <Button variant="contained" style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }} onClick={handleDownload}>
                {t('save-protocol')}
              </Button> */}
              <Button
                variant="contained"
                style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }}
                onClick={handleSubmit(onSubmit)}
              >
                {t('save')}
              </Button>
            </Box>
          </Stack>
        </form>
      </FormProvider>
    </Stack>
  )
}
